/**
 * IssuesKanban - Display issues as Kanban columns
 * Columns are dynamically generated from unique status names
 */

import { useMemo } from 'react';
import type { Issue } from '../types';
import './IssuesKanban.css';

interface IssuesKanbanProps {
  issues: Issue[];
}

interface Column {
  statusName: string;
  statusCategoryKey: string;
  issues: Issue[];
}

export function IssuesKanban({ issues }: IssuesKanbanProps) {
  // Group issues by status name and create columns
  const columns = useMemo(() => {
    const columnMap = new Map<string, Column>();

    // Sort issues by status category order: new -> indeterminate -> done
    const categoryOrder = { 'new': 0, 'indeterminate': 1, 'done': 2 };

    issues.forEach((issue) => {
      const statusName = issue.fields.status.name;
      const categoryKey = issue.fields.status.statusCategory.key;

      if (!columnMap.has(statusName)) {
        columnMap.set(statusName, {
          statusName,
          statusCategoryKey: categoryKey,
          issues: [],
        });
      }
      columnMap.get(statusName)!.issues.push(issue);
    });

    // Sort columns by category order
    return Array.from(columnMap.values()).sort((a, b) => {
      const orderA = categoryOrder[a.statusCategoryKey as keyof typeof categoryOrder] ?? 1;
      const orderB = categoryOrder[b.statusCategoryKey as keyof typeof categoryOrder] ?? 1;
      return orderA - orderB;
    });
  }, [issues]);

  if (columns.length === 0) {
    return (
      <div className="kanban-empty">
        <p>No issues to display</p>
      </div>
    );
  }

  return (
    <div className="kanban-board">
      {columns.map((column) => (
        <div
          key={column.statusName}
          className={`kanban-column kanban-column--${column.statusCategoryKey}`}
        >
          <div className="kanban-column-header">
            <span className="kanban-column-title">{column.statusName}</span>
            <span className="kanban-column-count">{column.issues.length}</span>
          </div>
          <div className="kanban-column-content">
            {column.issues.map((issue) => {
              const hasNoEstimate = issue.fields.storyPoints === undefined || issue.fields.storyPoints === null;
              return (
                <div
                  key={issue.id}
                  className={`kanban-card ${hasNoEstimate ? 'no-estimate' : ''}`}
                >
                  <div className="kanban-card-header">
                    <span className="kanban-card-key">{issue.key}</span>
                    <span className="kanban-card-points">
                      {hasNoEstimate ? (
                        <span className="no-points-warning">NO SP</span>
                      ) : (
                        `${issue.fields.storyPoints} SP`
                      )}
                    </span>
                  </div>
                  <p className="kanban-card-summary">{issue.fields.summary}</p>
                  {issue.fields.priority && (
                    <span className={`kanban-card-priority priority-${issue.fields.priority.name.toLowerCase()}`}>
                      {issue.fields.priority.name}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

export default IssuesKanban;
